<template>
  <div
    class="vue-app-date-picker reports-app-datepicker d-flex flex-wrap gap-10"
    :class="{ disabled }"
  >
    <a
      v-show="showQuickPick"
      v-tooltip="{ placement: 'top' }"
      class="prev btn btn-default btn-icon m-0"
      href="#"
      data-container="body"
      :disabled="previousDisabled ? 'disabled' : null"
      :data-original-title="previousDisabledTooltip"
      @click.prevent="gotoPreviousDate"
    >
      <i class="hi hi-left-arrow text-lighter" />
    </a>
    <a
      v-show="showQuickPick"
      v-tooltip="{ placement: 'top' }"
      class="next btn btn-default btn-icon m-0"
      href="#"
      data-container="body"
      :disabled="nextDisabled ? 'disabled' : null"
      :data-original-title="nextDisabledTooltip"
      @click.prevent="gotoNextDate"
    >
      <i class="hi hi-right-arrow text-lighter" />
    </a>
    <slot />
    <div class="flex-auto">
      <div
        v-if="!hideToday"
        v-show="showQuickPick"
        class="today d-block btn btn-default js-filter-date-actions"
        href="#"
        @click.prevent="gotoCurrent"
      >
        Today
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate, parseInternalDate, createWithZone } from '~/utils/time_utils';
import { isEmpty, isNil } from 'lodash-es';
import VModelCompat from '~/mixins/v_model_compat';

export default {
  mixins: [VModelCompat],
  props: {
    value: {
      type: Object,
    },
    modelValue: {
      type: Object,
    },
    type: {
      type: String,
      // supported: 'daily', 'weekly' and 'monthly'.
      default: 'weekly',
      validator: (value) => ['daily', 'weekly', 'monthly'].includes(value),
    },
    showQuickPick: {
      type: Boolean,
      default: true,
    },
    // This prop is related to the "gotoNextDate" arrow only.
    // It does NOT disable future dates in the calendar.
    // For disabling future dates we need to use the maxDate props.
    disableFuture: {
      type: Boolean,
      default: false,
    },
    hideToday: {
      type: Boolean,
      default: false,
    },
    startDate: {},
    endDate: {},
    minDate: {},
    maxDate: {},
    timeDataDates: [],
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    extraPickerClasses: {
      type: String,
      default: 'weekly-range-input margin-bottom-sm',
    },
    extraInputClasses: {
      type: String,
    },
    minDateTooltip: {
      type: String,
      default: 'Cannot go past minimum date',
    },
    startWeekOn: {
      default: 0,
    },
    noClear: {
      type: Boolean,
      default: true,
    },
    direction: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'center', 'right'].includes(value),
    },
  },
  data() {
    return {
      dateRangePicker: null,
    };
  },
  computed: {
    datepickerFormattedValue() {
      if (isNil(this.modelCompatValue)) {
        return;
      }

      return this.selectedDate || this.modelCompatValue._i;
    },
    daily() {
      return this.type === 'daily';
    },
    minimumDate() {
      if (isEmpty(this.minDate)) {
        return;
      }

      return this.modelCompatValue.tz ? createWithZone(this.minDate, null, this.modelCompatValue.tz()) : moment(this.minDate);
    },
    startDateOfWeek() {
      const startOfWeek = toMomentIsoWeekday(this.startWeekOn);

      return moment(this.modelCompatValue).startOfIsoWeek(startOfWeek);
    },
    stopDateOfWeek() {
      const startOfWeek = toMomentIsoWeekday(this.startWeekOn);

      return moment(this.modelCompatValue).endOfIsoWeek(startOfWeek);
    },
    previousDisabled() {
      let disabled;

      if (!isEmpty(this.minDate)) {
        let date = this.daily ? this.modelCompatValue : this.stopDateOfWeek;

        date = date.clone().subtract(this.period, 'days');
        disabled = this.isBeforeMinDate(date);
      }

      return disabled;
    },
    previousDisabledTooltip() {
      return this.previousDisabled ? this.minDateTooltip : null;
    },
    nextDisabled() {
      let disabled;

      if (this.disableFuture) {
        let date = this.daily ? this.modelCompatValue : this.startDateOfWeek;

        date = date.clone().add(this.period, 'days');
        disabled = this.isFuture(date);
      }

      return disabled;
    },
    nextDisabledTooltip() {
      let tooltip;

      if (this.nextDisabled) {
        tooltip = this.daily ? 'Date is in the future' : 'Date range is in the future';
      }

      return tooltip;
    },
    period() {
      return this.endDate.diff(this.startDate, 'days');
    },
    previousDate() {
      return [this.modelCompatValue.subtract(this.period + 1, 'days'), this.endDate.subtract(this.period + 1, 'days')];
    },
    nextDate() {
      return [this.modelCompatValue.add(this.period + 1, 'days'), this.endDate.add(this.period + 1, 'days')];
    },
    now() {
      return moment();
    },
    today() {
      return [moment(), moment()];
    },
    date() {
      if (this.type === 'weekly') {
        const date = this.startDateOfWeek;
        const startDate = formatDate(date);

        if (this.endDate) {
          const endDate = formatDate(date.add(6, 'day'));

          return `${startDate} - ${endDate}`;
        }

        return startDate;
      }

      if (this.type === 'monthly') {
        const start = moment(this.startDate).startOf('month');
        const stop = moment(start).endOf('month');

        return `${formatDate(start)} - ${formatDate(stop)}`;
      }

      const date = parseInternalDate(this.modelCompatValue);

      return formatDate(date);
    },
  },
  watch: {
    maxDate(newDate) {
      this.dateRangePicker.maxDate = moment(newDate);
    },
    value() {
      if (this.daily) {
        this.initialize();
      }
    },
  },
  methods: {
    isFuture(date) {
      return date.isAfter(this.now);
    },
    isBeforeMinDate(date) {
      return date.isBefore(this.minimumDate, 'day');
    },
    gotoDate(date) {
      if (!date) {
        return false;
      }

      this.emitModelCompatValue(date);
    },
    gotoPreviousDate() {
      if (!this.previousDisabled) {
        this.gotoDate(this.previousDate);
        this.analyticsTrack('Date arrow selected', { direction: 'backward' });
      }
    },
    gotoNextDate() {
      if (!this.nextDisabled) {
        this.gotoDate(this.nextDate);
        this.analyticsTrack('Date arrow selected', { direction: 'forward' });
      }
    },
    gotoCurrent() {
      this.gotoDate(this.today);
      this.analyticsTrack('Date today');
    },
    resetDate(date) {
      this.dateRangePicker.setStartDate(date);
      this.dateRangePicker.setEndDate(formatDate(date));
    },
    analyticsTrack(eventName, eventProperties) {
      window.analytics?.track(eventName, { type: 'range', ...eventProperties });
    },
  },
};
</script>
